<script lang="ts">
    /**
     * SpritePlayer – Pixar-quality CSS sprite-sheet animator.
     *
     * Renders exactly ONE frame at a time from a horizontal sprite strip.
     * Three animation modes inspired by the 12 principles of animation:
     *
     * • "action"  – Non-uniform frame timing + squash/stretch/bob.
     *               Peak pose (frame 4-5) holds longest for readability.
     *               Anticipation → Burst → Peak (hold) → Follow-through → Settle.
     *
     * • "uniform" – Equal time per frame via steps(N), no transforms. Clean loop.
     *
     * • "idle"    – Equal frame timing + gentle breathing bob.
     *
     * Props
     * ─────
     * src       – URL of the sprite sheet image
     * size      – pixel width of ONE visible frame (default 96)
     * frames    – number of frames in the sheet (default 8)
     * fps       – playback speed (default 8; try 5–10 for action mode)
     * playing   – whether animation is running (default true)
     * pixelated – crisp-edges rendering (default false)
     * cropY     – vertical crop fraction, centered (0.1–1.0, default 0.4)
     * mode      – animation style: 'action' | 'uniform' | 'idle' (default 'action')
     */

    let {
        src,
        size = 96,
        frames = 8,
        fps = 8,
        playing = true,
        pixelated = false,
        cropY = 0.4,
        mode = "action" as "action" | "uniform" | "idle",
    }: {
        src: string;
        size?: number;
        frames?: number;
        fps?: number;
        playing?: boolean;
        pixelated?: boolean;
        cropY?: number;
        mode?: "action" | "uniform" | "idle";
    } = $props();

    let naturalW = $state(0);
    let naturalH = $state(0);

    $effect(() => {
        const img = new Image();
        img.onload = () => {
            naturalW = img.naturalWidth;
            naturalH = img.naturalHeight;
        };
        img.src = src;
    });

    /** Per-frame aspect ratio (height / width) */
    const frameAspect = $derived(
        naturalW > 0 ? naturalH / (naturalW / frames) : 1,
    );

    /** Full pixel height of one frame at `size` px wide */
    const fullFrameH = $derived(Math.round(size * frameAspect));

    /** Clamped crop factor */
    const crop = $derived(Math.max(0.1, Math.min(1, cropY)));

    /** Viewport height: visible fraction of the frame, centred vertically */
    const viewH = $derived(Math.round(fullFrameH * crop));

    /** Total pixel shift (uniform mode keyframe "to" value) */
    const shift = $derived(-(frames * size));

    /** Duration of one full cycle (seconds) */
    const dur = $derived(frames / fps);

    /**
     * Composed CSS `animation` value including play-state.
     *
     * Action mode uses TWO layered animations:
     *   1) sprite-frames-action — frame cycling with steps(1,end) for
     *      non-uniform hold durations (peak frame holds longest).
     *   2) sprite-bounce-action — smooth squash/stretch/bob interpolated
     *      with per-segment easing curves.
     *
     * Uniform mode uses a single animation with steps(N).
     * Idle mode uses uniform frames + a slow breathing bob.
     */
    const animStr = $derived.by(() => {
        const ps = playing ? "running" : "paused";

        if (mode === "action") {
            return [
                `sprite-frames-action ${dur}s steps(1, end) infinite ${ps}`,
                `sprite-bounce-action ${dur}s ease-in-out infinite ${ps}`,
            ].join(", ");
        }

        if (mode === "idle") {
            return [
                `sprite-play ${dur}s steps(${frames}) infinite ${ps}`,
                `sprite-idle-bounce ${dur * 2.5}s ease-in-out infinite ${ps}`,
            ].join(", ");
        }

        // uniform
        return `sprite-play ${dur}s steps(${frames}) infinite ${ps}`;
    });
</script>

{#if naturalW > 0}
    <div
        class="sprite"
        class:crisp={pixelated}
        style:width="{size}px"
        style:height="{viewH}px"
        style:background-image="url({src})"
        style:background-size="{frames * size}px {fullFrameH}px"
        style:background-position="0 center"
        style:--fw="{size}px"
        style:--shift="{shift}px"
        style:animation={animStr}
        role="img"
        aria-label="Niji animation"
    ></div>
{:else}
    <!-- Placeholder while image dimensions load -->
    <div
        class="sprite loading"
        style:width="{size}px"
        style:height="{size}px"
    ></div>
{/if}

<style>
    /* ── Uniform: simple pixel-shift with steps(N) ─────────────────────── */
    @keyframes -global-sprite-play {
        from {
            background-position-x: 0;
        }
        to {
            background-position-x: var(--shift);
        }
    }

    /* ── Action: non-uniform frame timing (Pixar principles) ───────────── */
    /*
     * Frame hold durations (% of cycle):
     *   F1 rest 12%  ·  F2 anticipation 8%  ·  F3 burst 8%
     *   F4 PEAK 22%  ·  F5 follow-through 14%  ·  F6 settle 12%
     *   F7 ease-out 14%  ·  F8 rest 10%
     *
     * steps(1, end) = hold current frame value, snap at boundary.
     * var(--fw) = one frame width in px, set per-instance.
     */
    @keyframes -global-sprite-frames-action {
        0% {
            background-position-x: 0;
        }
        12% {
            background-position-x: calc(-1 * var(--fw));
        }
        20% {
            background-position-x: calc(-2 * var(--fw));
        }
        28% {
            background-position-x: calc(-3 * var(--fw));
        }
        50% {
            background-position-x: calc(-4 * var(--fw));
        }
        64% {
            background-position-x: calc(-5 * var(--fw));
        }
        76% {
            background-position-x: calc(-6 * var(--fw));
        }
        90%,
        100% {
            background-position-x: calc(-7 * var(--fw));
        }
    }

    /* ── Action: squash / stretch / bob (smoothly interpolated) ────────── */
    /*
     * Per-segment animation-timing-function gives organic Pixar feel:
     *   Anticipation → ease-out      (gentle wind-up)
     *   Burst → snap bezier           (quick acceleration)
     *   Peak → ease-in-out            (smooth suspension)
     *   Settle → ease-out             (gentle deceleration)
     *
     * Transform values are intentionally subtle — Pixar restraint.
     * transform-origin at 50% 85% anchors squash/stretch near the feet.
     */
    @keyframes -global-sprite-bounce-action {
        0% {
            transform: scaleX(1) scaleY(1) translateY(0);
            animation-timing-function: ease-out;
        }
        12% {
            /* Anticipation: squash into ground, coil energy */
            transform: scaleX(1.01) scaleY(0.98) translateY(1.5%);
            animation-timing-function: cubic-bezier(0.22, 1, 0.36, 1);
        }
        20% {
            /* Burst: stretch up, snap into action */
            transform: scaleX(0.99) scaleY(1.04) translateY(-2.5%);
            animation-timing-function: cubic-bezier(0.22, 1, 0.36, 1);
        }
        28% {
            /* Peak: slight stretch, lifted — held the longest */
            transform: scaleX(1) scaleY(1.02) translateY(-3.5%);
            animation-timing-function: ease-in-out;
        }
        50% {
            /* Follow-through: mass settling back down */
            transform: scaleX(1.005) scaleY(0.99) translateY(-1%);
            animation-timing-function: ease-in-out;
        }
        64% {
            /* Overshoot: tiny bounce past rest position */
            transform: scaleX(0.995) scaleY(1.015) translateY(0.8%);
            animation-timing-function: ease-out;
        }
        76% {
            /* Ease back to neutral */
            transform: scaleX(1) scaleY(1) translateY(0);
            animation-timing-function: ease-out;
        }
        90%,
        100% {
            /* Rest — seamless loop back to frame 1 */
            transform: scaleX(1) scaleY(1) translateY(0);
        }
    }

    /* ── Idle: gentle breathing bob ────────────────────────────────────── */
    @keyframes -global-sprite-idle-bounce {
        0%,
        100% {
            transform: scaleY(1) translateY(0);
        }
        50% {
            transform: scaleY(1.008) translateY(-1.2%);
        }
    }

    .sprite {
        overflow: hidden;
        background-repeat: no-repeat;
        transform-origin: 50% 85%;
        will-change: background-position, transform;
    }

    .loading {
        background: var(--backgroundMain, #f0f0f0);
        border-radius: 8px;
    }

    .crisp {
        image-rendering: pixelated;
        image-rendering: crisp-edges;
    }
</style>
